import React, { useState, useRef } from "react";
import { ChartIcon, ChevronRightIcon, ShareIcon, CopyLinkIcon } from "assets";
import { Button } from "components/button";
import { JobItem, useShareAssessmentDetails, useToast } from "hooks";
import { useNavigate } from "react-router-dom";
import { Routes } from "router";
import { useClickOutside } from "hooks";
import { copyToClipBoard } from "helpers";

interface StatsProps {
  jobTitle: string;
  totalTalents: number;
  averageScore: number;
  interviewReady: number;
  probeFurther: number;
  rejected: number;
  status: "Active" | "Inactive";
  allJobs: JobItem[];
  currentAssessmentId?: string;
  deadline?: string | null;
}

const formatDeadlineDisplay = (deadline?: string | null): string => {
  if (!deadline) return '-';
  const date = new Date(deadline);
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  const suffix = [11, 12, 13].includes(day)
    ? 'th'
    : day % 10 === 1
    ? 'st'
    : day % 10 === 2
    ? 'nd'
    : day % 10 === 3
    ? 'rd'
    : 'th';
  return `${month} ${day}${suffix}`;
};

const AssessmentStatistics: React.FC<StatsProps> = ({
  jobTitle,
  totalTalents,
  averageScore,
  interviewReady,
  probeFurther,
  rejected,
  status,
  allJobs,
  currentAssessmentId,
  deadline,
}) => {
  const navigate = useNavigate();
  const { openToast } = useToast();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [fetchShareEnabled, setFetchShareEnabled] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const shareModalRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => setShowDropdown(false));
  useClickOutside(shareModalRef, () => setShowShareModal(false));

  const { shareDetails, isLoading: shareLoading } = useShareAssessmentDetails(
    currentAssessmentId,
    fetchShareEnabled
  );
  const hasCandidates = totalTalents > 0;

  const handleJobSelect = (job: JobItem, jobIndex: number) => {
    const assessmentId = job.assessment_id || job.id?.toString() || jobIndex.toString();
    navigate(Routes.reviewTalents(assessmentId));
    setShowDropdown(false);
  };

  const handleShareClick = () => {
    setFetchShareEnabled(true);
    setShowShareModal(true);
  };

  const handleCopyLink = () => {
    const link = shareDetails?.application_link;
    if (!link) return;
    copyToClipBoard({
      link,
      onSuccess: () => {
        openToast({
          type: 'success',
          show: true,
          heading: 'Copied',
          text: 'Assessment link copied to clipboard',
        });
      },
      onError: () => {
        openToast({
          type: 'error',
          show: true,
          heading: 'Error',
          text: 'Failed to copy link',
        });
      },
    });
  };

  return (
    <section className="md:py-6 pt-6 pb-0 bg-[#f9fbfd] rounded-xl">
      {/* Header */}
      <div className="flex sm:flex-row flex-col justify-between sm:items-center sm:gap-0 gap-4 mb-5">
        <div className="relative" ref={dropdownRef}>
          <Button
            type='outline'
            onClick={() => setShowDropdown(!showDropdown)}
            className="!border-[#d1e9ff] !bg-white !rounded-md !py-2 !px-3.5 !text-sm !font-medium !text-[#012169] !ml-0 flex items-center gap-4 hover:!bg-transparent"
          >
            {shareDetails?.title ?? jobTitle}
            <ChevronRightIcon
              className={`mt-1 -rotate-90 transition-transform duration-300 [&_path]:stroke-[#012169] [&_path]:stroke-[1] ${showDropdown ? 'rotate-90' : ''}`}
            />
          </Button>

          {showDropdown && allJobs.length > 0 && (
            <div className="absolute top-full left-0 mt-2 bg-white border border-[#E4E7EC] rounded-lg shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)] min-w-[250px] max-h-[300px] overflow-y-auto z-[1000]">
              {allJobs.map((job, index) => {
                const jobAssessmentId = job.assessment_id || job.id?.toString();
                const isSelected = jobAssessmentId === currentAssessmentId;
                return (
                  <div
                    key={jobAssessmentId || index}
                    className={`px-4 py-3 text-sm text-[#344054] cursor-pointer transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg hover:bg-[#F9FAFB] ${isSelected ? 'bg-[#D1E9FF] !text-[#012169] font-medium' : ''}`}
                    onClick={() => handleJobSelect(job, index)}
                  >
                    {job.title}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 justify-between">
          <div className="relative" ref={shareModalRef}>
            <Button
              type="outline"
              onClick={handleShareClick}
              className="!border-[#d1e9ff] !bg-white !rounded-lg !py-2.5 !px-4 sm:!py-2.5 sm:!px-3 !text-sm sm:!text-xs !font-medium !text-[#012169] !ml-0 flex items-center gap-2 hover:!bg-[#f0f7ff]"
            >
              <ShareIcon className="w-[18px] h-[18px] sm:w-[15px] sm:h-[15px] [&_path]:stroke-[#012169]" />
              Share job post
            </Button>

            {showShareModal && (
              <div className="absolute top-[calc(100%+8px)] right-0 bg-white border border-[#e4e7ec] rounded-xl shadow-[0px_8px_24px_rgba(0,0,0,0.12)] p-4 min-w-[300px] z-[1000]">
                <p className="text-sm font-semibold text-[#101828] mb-2">Share Assessment Link</p>
                {shareLoading ? (
                  <p className="text-[13px] text-[#667085] text-center py-2">Loading...</p>
                ) : shareDetails ? (
                  <>
                    <p className="text-[13px] text-[#475467] mb-2.5">{shareDetails.title}</p>
                    <div className="bg-[#f9fafb] border border-[#eaecf0] rounded-md px-2.5 py-2 mb-2.5 overflow-hidden">
                      <span className="text-xs text-[#667085] break-all block">{shareDetails.application_link}</span>
                    </div>
                    <button
                      className="flex items-center justify-center gap-1.5 bg-transparent border border-[#012169] rounded-md py-[7px] px-3.5 text-[13px] font-medium text-[#012169] cursor-pointer w-full transition-colors duration-150 hover:bg-[#f0f7ff]"
                      onClick={handleCopyLink}
                    >
                      <CopyLinkIcon className="w-[15px] h-[15px] [&_path]:stroke-[#012169]" />
                      Copy Link
                    </button>
                  </>
                ) : (
                  <p className="text-[13px] text-[#667085] text-center py-2">Unable to load share details.</p>
                )}
              </div>
            )}
          </div>

          <Button
            type="fill"
            disabled={!hasCandidates}
            data-tour-id="tour-analytics-stats"
            onClick={() => {
              if (currentAssessmentId && hasCandidates) {
                navigate(Routes.analytics(currentAssessmentId));
              }
            }}
            className="!bg-[#012169] !text-white !border-none !rounded-lg !py-2.5 !px-4 sm:!py-2.5 sm:!px-3 !text-sm sm:!text-xs !font-medium !ml-0 flex items-center gap-2"
          >
            <ChartIcon className="w-5 h-5 sm:w-[15px] sm:h-[15px] [&_path]:fill-white" />
            View Analytics
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="md:flex hidden bg-white rounded-xl p-5 items-center justify-between shadow-[0px_2px_4px_0px_#0000000D]">
        <div className="flex flex-col min-w-[100px]">
          <span className="text-base text-[#475467] mb-1.5">Total Talents</span>
          <span className="text-2xl font-semibold text-[#101828]">{totalTalents}</span>
        </div>
        <div className="w-px h-10 bg-[#eaecf0]" />
        <div className="flex flex-col min-w-[100px]">
          <span className="text-base text-[#475467] mb-1.5">Average score</span>
          <span className="text-2xl font-semibold text-[#101828]">{averageScore}%</span>
        </div>
        <div className="w-px h-10 bg-[#eaecf0]" />
        <div className="flex flex-col min-w-[100px]">
          <span className="text-base text-[#475467] mb-1.5">Interview Ready</span>
          <span className="text-2xl font-semibold text-[#101828]">{interviewReady}</span>
        </div>
        <div className="w-px h-10 bg-[#eaecf0]" />
        <div className="flex flex-col min-w-[100px]">
          <span className="text-base text-[#475467] mb-1.5">Probe further</span>
          <span className="text-2xl font-semibold text-[#101828]">{probeFurther}</span>
        </div>
        <div className="w-px h-10 bg-[#eaecf0]" />
        <div className="flex flex-col min-w-[100px]">
          <span className="text-base text-[#475467] mb-1.5">Rejected</span>
          <span className="text-2xl font-semibold text-[#101828]">{rejected}</span>
        </div>
        <div className="w-px h-10 bg-[#eaecf0]" />
        <div className="flex flex-col min-w-[100px]">
          <span className="text-base text-[#475467] mb-1.5">Status</span>
          <span className={`text-sm px-2.5 py-1 rounded-xl w-fit ${status === "Active" ? "bg-[#E6FFF3B2] text-[#069952]" : "bg-[#fef3f2] text-[#b42318]"}`}>
            {status}
          </span>
        </div>
        <div className="w-px h-10 bg-[#eaecf0]" />
        <div className="flex flex-col min-w-[100px]">
          <span className="text-base text-[#475467] mb-1.5">Deadline</span>
          <span className="text-sm px-2.5 py-1 rounded-xl bg-[#f2f4f7] text-[#344054] w-fit font-medium">
            {formatDeadlineDisplay(deadline)}
          </span>
        </div>
      </div>
    </section>
  );
};

export { AssessmentStatistics };
